import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { DocumentData, limit, orderBy, QueryConstraint, QuerySnapshot, Unsubscribe, where } from 'firebase/firestore';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { MessagingService } from 'src/app/services/messaging/messaging.service';
import { MuteService } from 'src/app/services/mute/mute.service';
import { OrgSwitcherService } from 'src/app/services/org-switcher/org-switcher.service';
import { OrgTabsService } from 'src/app/services/org-tabs/org-tabs.service';
import { UserService } from 'src/app/services/user/user.service';
import { managerRosterKeys, ProjectManagerFields } from 'src/app/services/teams/roster-keys';
import { BackgroundConversation, nextCountDecision, orgTotal, railShowsInBackground, RailVisibility, tabCount } from './org-unread.util';

/** Same windows as MessagingListenerService's WATCHED queries — identical queries share one Firestore target. */
const AI_MAX = 200;
const CHANNEL_MAX = 50;

/** A project doc as the background rail needs it. */
type ProjectDoc = ProjectManagerFields & { conversationId?: string };

/**
 * The unread count on each open org tab (Vincent, 2026-10-22: "le # de requête
 * en attente par Co"). The org you are in shows NO count — you are looking at
 * its rail (Vincent: "when a tab is selected, I shouldn't see the counter").
 * The other open orgs are counted here, quietly (no notifications or sounds),
 * over exactly the rows their rail would hold (railShowsInBackground — project
 * managers hidden, project spaces shown): agent DMs and spaces come from the
 * same user-wide queries the listener already runs (they are not org-filtered
 * server-side), and each background org's projects from two small
 * membership-scoped listeners (the managers and project spaces). A
 * conversation is re-read only when its last message changes, team traffic
 * never counts, and nothing is listened to unless another org is open.
 */
@Injectable({ providedIn: 'root' })
export class OrgUnreadService {
  private readonly firebase = inject(FirebaseService);
  private readonly messaging = inject(MessagingService);
  private readonly mute = inject(MuteService);
  private readonly switcher = inject(OrgSwitcherService);
  private readonly orgTabs = inject(OrgTabsService);
  private readonly user = inject(UserService);

  private readonly backgroundTotals = signal<ReadonlyMap<string, number>>(new Map());

  /** Open orgs other than the one you are in. */
  private readonly backgroundIds = computed(() => {
    const current = this.switcher.currentOrgId();
    return this.orgTabs.openOrgIds().filter((id) => id !== current);
  });

  private sharedUnsubs: Unsubscribe[] = [];
  private readonly projectUnsubs = new Map<string, Unsubscribe[]>();
  private aiDocs: BackgroundConversation[] = [];
  private channelDocs: BackgroundConversation[] = [];
  /** Per background org: its projects (member-of and created-by listeners, merged by id). */
  private readonly projects = new Map<string, { member: Map<string, ProjectDoc>; created: Map<string, ProjectDoc> }>();
  /** Per background org: each counted conversation's last message id and count. */
  private readonly state = new Map<string, Map<string, { lastMessageId?: string; count: number }>>();
  /** conversationId → the person's read marker (null = never opened). Cleared for an org when you leave it. */
  private readonly readMarkers = new Map<string, string | null>();
  private watched = new Set<string>();
  private queue: Promise<void> = Promise.resolve();

  constructor() {
    effect(() => this.sync(this.backgroundIds()));
    inject(DestroyRef).onDestroy(() => this.stopAll());
  }

  /** The circle for an org's tab: undefined (nothing — always for the org you are in), 1–9 or '9+'. */
  public countFor(orgId: string): number | '9+' | undefined {
    if (orgId === this.switcher.currentOrgId()) return undefined;
    return tabCount(this.backgroundTotals().get(orgId) ?? 0);
  }

  private sync(ids: string[]): void {
    const uid = this.user.user?.uid;
    if (!uid || !ids.length) {
      this.stopAll();
      return;
    }
    const next = new Set(ids);
    for (const orgId of this.watched) {
      if (next.has(orgId)) continue;
      // Closed, or now the org you are in: its own rail takes over.
      this.projectUnsubs.get(orgId)?.forEach((u) => u());
      this.projectUnsubs.delete(orgId);
      this.projects.delete(orgId);
      this.state.delete(orgId);
    }
    for (const orgId of next) {
      if (this.watched.has(orgId)) continue;
      // Just left this org: what you read there must be re-read, not cached.
      this.forgetReadMarkers(orgId);
      // Same two membership-scoped queries as TeamsService (no composite index).
      const entry = { member: new Map<string, ProjectDoc>(), created: new Map<string, ProjectDoc>() };
      this.projects.set(orgId, entry);
      this.projectUnsubs.set(orgId, [
        this.listenProjects(orgId, where('userIds', 'array-contains', uid), entry.member),
        this.listenProjects(orgId, where('createdBy', '==', uid), entry.created),
      ]);
    }
    this.watched = next;
    if (!this.sharedUnsubs.length) {
      this.sharedUnsubs = [
        this.listen(sharedQuery(uid, 'ai', AI_MAX), (docs) => { this.aiDocs = docs; }),
        this.listen(sharedQuery(uid, 'channel', CHANNEL_MAX), (docs) => { this.channelDocs = docs; }),
      ];
    }
    this.publish();
  }

  private listen(constraints: QueryConstraint[], apply: (docs: BackgroundConversation[]) => void): Unsubscribe {
    return this.firebase.firestore.collectionListen('conversations', constraints, (snapshot) => {
      apply(toConversations(snapshot));
      this.schedule();
    });
  }

  private listenProjects(orgId: string, constraint: QueryConstraint, into: Map<string, ProjectDoc>): Unsubscribe {
    return this.firebase.firestore.collectionListen(`organizations/${orgId}/teams`, [constraint], (snapshot) => {
      into.clear();
      for (const doc of snapshot.docs) into.set(doc.id, doc.data() as ProjectDoc);
      this.schedule();
    });
  }

  /** The rail an org would show, from its projects. */
  private railFor(orgId: string): RailVisibility {
    const entry = this.projects.get(orgId);
    const all = entry ? [...new Map([...entry.member, ...entry.created]).values()] : [];
    return {
      managers: managerRosterKeys(all),
      projectSpaceIds: new Set(all.map((p) => p.conversationId).filter((id): id is string => !!id)),
    };
  }

  private schedule(): void {
    this.queue = this.queue
      .then(() => this.recompute())
      .catch((error) => console.error('[org-unread] recompute failed', error));
  }

  private async recompute(): Promise<void> {
    const uid = this.user.user?.uid;
    if (!uid) return;
    const muted = this.mute.mutedIds$.getValue();
    for (const orgId of this.watched) {
      const rail = this.railFor(orgId);
      const byId = new Map<string, BackgroundConversation>();
      for (const c of [...this.aiDocs, ...this.channelDocs]) {
        if (c.organizationId === orgId && !muted.has(c.id) && railShowsInBackground(c, rail)) byId.set(c.id, c);
      }
      await this.fetchMissingReadMarkers([...byId.keys()], uid);
      const previous = this.state.get(orgId) ?? new Map();
      const next = new Map<string, { lastMessageId?: string; count: number }>();
      await Promise.all([...byId.values()].map(async (c) => {
        const marker = this.readMarkers.get(c.id) ?? null;
        const decision = nextCountDecision(c, previous.get(c.id), uid);
        const count = decision.kind === 'keep' ? decision.count
          : decision.kind === 'zero' ? 0
          : marker ? await this.messaging.getMessagesCountAfter(c.id, marker) : await this.messaging.countUnreadInLatest(c.id);
        next.set(c.id, { lastMessageId: c.lastMessage?.id, count });
      }));
      if (this.watched.has(orgId)) this.state.set(orgId, next);
    }
    this.publish();
  }

  private async fetchMissingReadMarkers(ids: string[], uid: string): Promise<void> {
    const missing = ids.filter((id) => !this.readMarkers.has(id));
    await Promise.all(missing.map(async (id) => {
      const marker = await this.messaging.fetchLastReadMessageId(id, uid).catch(() => undefined);
      this.readMarkers.set(id, marker || null);
    }));
  }

  private forgetReadMarkers(orgId: string): void {
    for (const c of [...this.aiDocs, ...this.channelDocs]) {
      if (c.organizationId === orgId) this.readMarkers.delete(c.id);
    }
  }

  private publish(): void {
    const totals = new Map<string, number>();
    for (const orgId of this.watched) {
      totals.set(orgId, orgTotal([...(this.state.get(orgId)?.values() ?? [])].map((s) => s.count)));
    }
    this.backgroundTotals.set(totals);
  }

  private stopAll(): void {
    this.sharedUnsubs.forEach((u) => u());
    this.sharedUnsubs = [];
    this.projectUnsubs.forEach((unsubs) => unsubs.forEach((u) => u()));
    this.projectUnsubs.clear();
    this.projects.clear();
    this.aiDocs = [];
    this.channelDocs = [];
    this.state.clear();
    this.readMarkers.clear();
    this.watched = new Set();
    this.backgroundTotals.set(new Map());
  }
}

function sharedQuery(uid: string, category: 'ai' | 'channel', max: number): QueryConstraint[] {
  return [
    where('userIds', 'array-contains', uid),
    where('category', '==', category),
    where('closed', '==', false),
    orderBy('updatedOn', 'desc'),
    limit(max),
  ];
}

function toConversations(snapshot: QuerySnapshot<DocumentData>): BackgroundConversation[] {
  return snapshot.docs.map((doc) => {
    const d = doc.data();
    const last = d.lastMessage;
    return {
      id: doc.id,
      category: d.category,
      organizationId: d.organizationId ?? null,
      closed: d.closed === true,
      agentId: d.agentId ?? null,
      sharedAgentId: d.sharedAgentId ?? null,
      lastMessage: last ? { id: last.id, sender: last.sender, teamTraffic: last.teamTraffic === true } : null,
    };
  });
}
